import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MOINS, formaterDate, formaterMontant } from '@gsm/partage';
import { Bloc, Tableau, type Colonne } from './Tableau';
import { Nombre } from './Nombre';
import { Pastille } from './Pastille';
import { Pagination, type PageDe } from './Filtres';
import { useRessource } from '../api/ressource';
import { Note } from './Note';

export interface MouvementEspece {
  id: string;
  dateValeur: string;
  sens: 'CREDIT' | 'DEBIT';
  type: string;
  montant: string;
  soldeApres: string;
  libelle: string;
  annuleParId: string | null;
}

export const LIBELLE_MOUVEMENT: Record<string, string> = {
  VERSEMENT: 'Versement',
  RETRAIT: 'Retrait',
  ACHAT_TITRE: 'Achat de titres',
  VENTE_TITRE: 'Vente de titres',
  DIVIDENDE: 'Dividende',
  COUPON: 'Coupon',
  REMBOURSEMENT: 'Remboursement',
  DISTRIBUTION_OPCVM: 'Distribution OPCVM',
  FRAIS_GESTION: 'Frais de gestion',
  FRAIS_COURTAGE: 'Frais de courtage',
  REGULARISATION: 'Régularisation',
};

/**
 * Montant signé. Le signe porte le sens, jamais la couleur : le vert et le
 * rouge sont réservés à la performance (charte, § 2).
 */
export function montantSigne(sens: 'CREDIT' | 'DEBIT' | 'ENTREE' | 'SORTIE', valeur: string) {
  const sortant = sens === 'DEBIT' || sens === 'SORTIE';
  return (
    <span className="n">
      {sortant ? MOINS : '+'}
      {formaterMontant(valeur)}
    </span>
  );
}

/**
 * Chaîne des mouvements d'espèces d'un mandat, lue par pages.
 *
 * Partagée entre la fiche trésorerie, qui la parcourt en entier, et la fiche
 * portefeuille, qui n'en montre que la première page : une seule définition
 * des colonnes, donc une seule lecture possible.
 */
export function JournalEspeces({
  mandatId,
  taille = 50,
  apercu = false,
  lienFiche,
}: {
  mandatId: string;
  taille?: number;
  /** Première page seule, sans navigation ; le lien mène au journal complet. */
  apercu?: boolean;
  lienFiche?: string;
}) {
  const [page, setPage] = useState(1);
  const { donnees, chargement } = useRessource<PageDe<MouvementEspece>>(
    `/portefeuilles/${mandatId}/mouvements?page=${page}&taille=${taille}`,
  );
  const total = donnees?.total ?? 0;
  const tronque = apercu && total > (donnees?.elements.length ?? 0);

  return (
    <Bloc
      titre="Journal des espèces"
      meta={chargement ? 'chargement…' : `${total} mouvement(s)`}
      actions={
        lienFiche && (
          <Link className="bouton bouton--compact bouton--fantome" to={lienFiche}>
            Fiche trésorerie
          </Link>
        )
      }
    >
      <Tableau
        colonnes={COLONNES}
        lignes={donnees?.elements ?? []}
        cle={(x) => x.id}
        vide="Aucun mouvement d’espèces sur ce mandat."
      />
      {!apercu && donnees && (
        <Pagination page={donnees.page} taille={donnees.taille} total={total} onPage={setPage} />
      )}
      {tronque ? (
        <p className="note">
          Les {donnees?.elements.length} dernières écritures sur {total}. La fiche trésorerie donne
          la chaîne entière et les engagements à venir.
        </p>
      ) : (
        <Note resume="Chaque écriture porte le solde qu’elle laisse derrière elle.">
          La chaîne se relit de haut en bas et toute rupture se voit. Elle reproduit le journal reçu
          du back-office ; une correction arrive par ré-import de la situation.
        </Note>
      )}
    </Bloc>
  );
}

const COLONNES: Colonne<MouvementEspece>[] = [
  { cle: 'date', entete: 'Date de valeur', num: true, rendu: (x) => formaterDate(x.dateValeur) },
  { cle: 'type', entete: 'Type', rendu: (x) => LIBELLE_MOUVEMENT[x.type] ?? x.type },
  {
    cle: 'libelle',
    entete: 'Libellé',
    rendu: (x) => (
      <>
        {x.libelle}
        {x.annuleParId && (
          <>
            {' '}
            <Pastille ton="attente">Contre-passé</Pastille>
          </>
        )}
      </>
    ),
  },
  { cle: 'montant', entete: 'Mouvement', num: true, rendu: (x) => montantSigne(x.sens, x.montant) },
  {
    cle: 'solde',
    entete: 'Solde après',
    num: true,
    rendu: (x) => <Nombre valeur={x.soldeApres} />,
  },
];
